/**
 * StatWidget - Display single metric values with optional trends
 */

import React from "react";
import PropTypes from "prop-types";
import { Box, alpha } from "@mui/material";
import { TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon } from "@mui/icons-material";
import KDTypography from "components/KDTypography";
import BaseWidget from "./BaseWidget";
import { useMaterialUIController } from "context";
import { useDashboard } from "../context/DashboardContext";
import { getWidgetById } from "../config/widgetRegistry";

const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  const keys = path.split(".");
  let current = obj;
  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    current = current[key];
  }
  return current;
};

const formatValue = (value, format) => {
  if (value === null || value === undefined) return "—";

  switch (format) {
    case "number":
      if (typeof value === "number") {
        if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
        if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
        return value.toLocaleString();
      }
      return value;

    case "currency":
      if (typeof value === "number") {
        return new Intl.NumberFormat("en-CA", {
          style: "currency",
          currency: "CAD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      }
      return value;

    case "percentage":
    case "percent":
      if (typeof value === "number") return `${value.toFixed(1)}%`;
      return value;

    case "cents":
      if (typeof value === "number") return `${value.toFixed(1)}¢`;
      return value;

    case "milliseconds":
      if (typeof value === "number") {
        if (value < 1000) return `${value}ms`;
        return `${(value / 1000).toFixed(2)}s`;
      }
      return value;

    case "bytes":
      if (typeof value === "number") {
        const units = ["B", "KB", "MB", "GB", "TB"];
        let unitIndex = 0;
        let size = value;
        while (size >= 1024 && unitIndex < units.length - 1) {
          size /= 1024;
          unitIndex++;
        }
        return `${size.toFixed(1)} ${units[unitIndex]}`;
      }
      return value;

    case "status":
      if (value === true || value === "healthy" || value === "online") return "Healthy";
      if (value === false || value === "unhealthy" || value === "offline") return "Unhealthy";
      return value;

    case "duration":
      if (typeof value === "number") {
        if (value < 60) return `${Math.round(value)}s`;
        if (value < 3600) return `${Math.floor(value / 60)}m ${Math.round(value % 60)}s`;
        return `${Math.floor(value / 3600)}h ${Math.floor((value % 3600) / 60)}m`;
      }
      return value;

    default:
      return value;
  }
};

function TrendIndicator({ value, previousValue, inverted = false }) {
  if (previousValue === null || previousValue === undefined || previousValue === 0) return null;

  const change = ((value - previousValue) / previousValue) * 100;
  const isPositive = inverted ? change < 0 : change > 0;
  const isNegative = inverted ? change > 0 : change < 0;
  const color = isPositive ? "#10B981" : isNegative ? "#EF4444" : "rgba(255,255,255,0.5)";
  const Icon = change >= 0 ? TrendingUpIcon : TrendingDownIcon;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: "8px",
        bgcolor: alpha(color, 0.15),
      }}
    >
      <Icon sx={{ color, fontSize: 14 }} />
      <KDTypography variant="caption" sx={{ color, fontWeight: 600 }}>
        {Math.abs(change).toFixed(1)}%
      </KDTypography>
    </Box>
  );
}

TrendIndicator.propTypes = {
  value: PropTypes.number,
  previousValue: PropTypes.number,
  inverted: PropTypes.bool,
};

function StatWidget({
  widgetId,
  value: propValue,
  previousValue: propPreviousValue,
  loading: propLoading,
  error: propError,
  isExpanded = false,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { metricsData } = useDashboard();
  const widget = getWidgetById(widgetId);

  if (!widget) return null;

  const categoryData = metricsData[widget.category];
  const dataValue = propValue ?? getNestedValue(categoryData, widget.dataKey) ?? getNestedValue(categoryData?.data, widget.dataKey);
  const previousValue = propPreviousValue ?? getNestedValue(categoryData?.previousPeriod, widget.dataKey);
  const loading = propLoading ?? categoryData === undefined;
  const error = propError ?? null;

  const formattedValue = formatValue(dataValue, widget.format);
  const showTrend = widget.showTrend && previousValue !== undefined;
  const invertedTrend = widget.dataKey?.includes("churn") || widget.dataKey?.includes("error");

  return (
    <BaseWidget
      id={widgetId}
      title={widget.title}
      icon={widget.icon}
      color={widget.categoryColor}
      gradient={widget.categoryGradient}
      loading={loading}
      error={error}
      showDragHandle
      isExpanded={isExpanded}
      headerExtra={
        showTrend && typeof dataValue === "number" ? (
          <TrendIndicator value={dataValue} previousValue={previousValue} inverted={invertedTrend} />
        ) : null
      }
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <KDTypography
          variant="h2"
          sx={{
            color: darkMode ? "white" : "rgba(0,0,0,0.87)",
            fontWeight: 700,
            lineHeight: 1,
            mb: 0.5,
            fontSize: { xs: "1.75rem", sm: "2rem", md: "2.5rem" },
          }}
        >
          {formattedValue}
        </KDTypography>
        <KDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)", fontSize: "0.75rem" }}>
          {widget.description}
        </KDTypography>
      </Box>
    </BaseWidget>
  );
}

StatWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  previousValue: PropTypes.number,
  loading: PropTypes.bool,
  error: PropTypes.shape({ message: PropTypes.string }),
  isExpanded: PropTypes.bool,
};

export default StatWidget;
